/**
 * Unified tombstone TTLs for calendar delete protection.
 * Slice (Redux) and socket-layer guards share these values.
 */

export const CALENDAR_DELETE_TOMBSTONE_TTL_MS = 45 * 1000;
/** Keep Redux echo window aligned with CalendarStore.LOCAL_ECHO_DEFAULT_MS. */
export const CALENDAR_LOCAL_ECHO_TTL_MS = 60 * 1000;

export function pruneExpiredTombstones(map, now = Date.now()) {
   if (!map || typeof map !== "object") return;
   for (const key of Object.keys(map)) {
      const until = Number(map[key]);
      if (!Number.isFinite(until) || until <= now) delete map[key];
   }
}

export function isTombstonedId(map, id, now = Date.now()) {
   if (!map || typeof map !== "object") return false;
   const key = String(id ?? "").trim();
   if (!key) return false;
   const until = Number(map[key]);
   if (!Number.isFinite(until) || until <= now) return false;
   return true;
}

export function markTombstonedId(map, id, now = Date.now()) {
   if (!map || typeof map !== "object") return;
   const key = String(id ?? "").trim();
   if (!key) return;
   map[key] = now + CALENDAR_DELETE_TOMBSTONE_TTL_MS;
}

export function clearTombstonedId(map, id) {
   if (!map || typeof map !== "object") return;
   const key = String(id ?? "").trim();
   if (!key) return;
   if (Object.prototype.hasOwnProperty.call(map, key)) delete map[key];
}

/**
 * In-memory tombstone registry (used by socket layer in calendar UI).
 */
export class TombstoneRegistry {
   constructor(ttlMs = CALENDAR_DELETE_TOMBSTONE_TTL_MS) {
      this._ttlMs = ttlMs;
      this._map = new Map();
   }

   mark(ids, now = Date.now()) {
      const list = Array.isArray(ids) ? ids : [ids];
      const expireAt = now + this._ttlMs;
      for (const value of list) {
         const id = String(value ?? "").trim();
         if (id) this._map.set(id, expireAt);
      }
   }

   clear(ids) {
      const list = Array.isArray(ids) ? ids : [ids];
      for (const value of list) {
         const id = String(value ?? "").trim();
         if (id) this._map.delete(id);
      }
   }

   has(id, now = Date.now()) {
      const key = String(id ?? "").trim();
      if (!key) return false;
      const expiresAt = Number(this._map.get(key) || 0);
      if (!expiresAt) return false;
      if (expiresAt <= now) {
         this._map.delete(key);
         return false;
      }
      return true;
   }

   prune(now = Date.now()) {
      for (const [key, expiresAt] of this._map.entries()) {
         if (Number(expiresAt) <= now) this._map.delete(key);
      }
   }
}
